import axios from "axios";
import { isVideoFile } from "../config/uploadLimits";

const VIDEO_UPLOAD_TIMEOUT_MS = 600000;
const IMAGE_UPLOAD_TIMEOUT_MS = 120000;

export function getUploadErrorMessage(err) {
  if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
    return "Upload failed due to a network error. Check your connection and try again.";
  }
  if (err?.code === "ECONNABORTED") {
    return "Upload timed out. Try a smaller file or a faster connection.";
  }
  return (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    "Upload error"
  );
}

/** Upload a single promo video through the backend (no direct S3 PUT). */
async function uploadVideoViaBackend({ file, customerId, modeId, title, apiBase }) {
  const formData = new FormData();
  formData.append("video", file);
  formData.append("customerId", customerId);
  formData.append("modeId", modeId);
  formData.append("title", title);

  const { data } = await axios.post(`${apiBase}/ads/upload`, formData, {
    timeout: VIDEO_UPLOAD_TIMEOUT_MS,
  });

  if (!data?.success) {
    throw new Error(data?.message || "Failed to upload video");
  }

  return data;
}

export async function uploadPromoMediaBulk({
  files,
  customerId,
  modeId,
  title,
  apiBase,
}) {
  const videos = files.filter((file) => isVideoFile(file));
  const images = files.filter((file) => !isVideoFile(file));

  for (const file of videos) {
    await uploadVideoViaBackend({ file, customerId, modeId, title, apiBase });
  }

  if (images.length > 0) {
    const formData = new FormData();
    images.forEach((file) => formData.append("file", file));
    formData.append("customerId", customerId);
    formData.append("modeId", modeId);
    formData.append("title", title);

    const { data } = await axios.post(`${apiBase}/addPromoAds`, formData, {
      timeout: IMAGE_UPLOAD_TIMEOUT_MS,
    });

    if (!data?.success) {
      throw new Error(data?.message || "Failed to upload images");
    }
  }
}

export async function updatePromoWithVideo({
  file,
  customerId,
  promoId,
  title,
  apiBase,
}) {
  const formData = new FormData();
  formData.append("_id", promoId);
  formData.append("title", title);
  formData.append("customerId", customerId);
  formData.append("file", file);

  const { data } = await axios.post(`${apiBase}/editPromo`, formData, {
    timeout: VIDEO_UPLOAD_TIMEOUT_MS,
  });

  if (!data?.success) {
    throw new Error(data?.message || "Failed to update promo");
  }

  return data;
}

export async function uploadSingleVideo({
  file,
  customerId,
  modeId,
  title,
  apiBase,
  onProgress,
}) {
  const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
  if (file.size > MAX_VIDEO_BYTES) {
    throw new Error(`${file.name} exceeds 50MB video size limit.`);
  }

  const CHUNK_SIZE = 900 * 1024; // 900KB per chunk, safely under 1MB Nginx limit
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const uploadId = `${customerId}-${Date.now()}-${safeFilename}`;

  console.log(`Chunked upload: ${totalChunks} chunks for ${file.name}`);

  // Phase 1: Upload all chunks
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);

    const form = new FormData();
    form.append("uploadId", uploadId);
    form.append("chunkIndex", i);
    form.append("totalChunks", totalChunks);
    form.append("chunk", blob, file.name);
    form.append("customerId", customerId);
    form.append("modeId", modeId);
    form.append("title", title || "None");
    form.append("mimeType", file.type);

    let attempts = 0;
    while (attempts < 3) {
      try {
        await axios.post(`${apiBase}/ads/upload-chunk`, form, {
          timeout: 30000,
        });
        break;
      } catch (err) {
        attempts++;
        if (attempts >= 3) {
          throw new Error(
            `Chunk ${i + 1}/${totalChunks} failed: ${err.message}`,
          );
        }
        await new Promise((r) => setTimeout(r, 1000 * attempts));
      }
    }

    // Progress 0% → 80% during chunk uploads
    const pct = Math.round(((i + 1) / totalChunks) * 80);
    if (onProgress) onProgress(pct, `Uploading part ${i + 1} of ${totalChunks}`);
  }

  // Phase 2: Finalize — backend reassembles and uploads to S3
  if (onProgress) onProgress(85, "Saving video...");

  const { data } = await axios.post(
    `${apiBase}/ads/upload-chunk-finalize`,
    {
      uploadId,
      customerId,
      modeId,
      title: title || "None",
      originalName: file.name,
      mimeType: file.type,
    },
    { timeout: 10 * 60 * 1000 }, // 10 min for S3 upload
  );

  if (!data?.success) {
    throw new Error(data?.message || "Finalize failed");
  }

  if (onProgress) onProgress(100, "Done!");
  return data;
}
